import cc from 'classcat'
import {
  ButtonHTMLAttributes,
  forwardRef,
  HTMLAttributeAnchorTarget,
  MouseEvent,
  ReactElement,
  ReactNode,
  Ref,
  useCallback,
  useMemo,
} from 'react'

import { Asable } from '../../types/asable'
import { LinkProps } from '../../types/linkProps'
import { getLinkProps } from '../../utils/getLinkProps'
import { IconName } from '../Icon'
import { Space } from '../Space'
import { ButtonIcon, ButtonIconProps } from './ButtonIcon'
import * as Styled from './styles'
import { ButtonColor, Size, Variant } from './types'

export type ButtonSize = Size
export type ButtonVariant = Variant

export interface ButtonProps extends ButtonHTMLAttributes<HTMLButtonElement>, Asable {
  active?: boolean
  centered?: boolean
  children?: ReactNode
  color?: ButtonColor
  disabled?: boolean
  focused?: boolean
  forceSquareRatio?: boolean
  fullRound?: boolean
  fullWidth?: boolean
  href?: string
  icon?: IconName | ReactElement
  iconRight?: IconName | ReactElement
  loading?: boolean
  size?: Size
  skipLeftRounding?: boolean
  skipRightRounding?: boolean
  target?: HTMLAttributeAnchorTarget
  to?: string
  variant?: Variant
  borderRadius?: string
}

export const Button = forwardRef(
  (
    {
      as,
      active,
      centered = true,
      children,
      color = 'primary',
      disabled,
      focused,
      forceSquareRatio,
      href,
      icon,
      iconRight,
      loading,
      onClick,
      size = 'md',
      skipLeftRounding,
      skipRightRounding,
      target,
      to,
      type = 'button',
      variant = 'filled',
      borderRadius,
      ...rest
    }: ButtonProps,
    forwardedRef: Ref<HTMLButtonElement>,
  ): ReactElement => {
    const buttonIconProps = useMemo(
      (): Pick<ButtonIconProps, 'size' | 'withIconMargin'> => ({
        size,
        withIconMargin: !!children,
      }),
      [children, size],
    )

    const handleClick = useCallback(
      (event: MouseEvent<HTMLButtonElement>) => {
        if (loading || disabled) {
          event.preventDefault()
          return
        }

        onClick?.(event)
      },
      [disabled, loading, onClick],
    )

    const linkProps: LinkProps | undefined = useMemo(
      () =>
        getLinkProps({
          as,
          href,
          to,
          target,
        }),
      [href, to, as, target],
    )

    return (
      <Styled.Button
        {...rest}
        {...linkProps}
        active={active}
        className={cc(['ds-button', rest.className, variant, color, { active, disabled, focused }])}
        centered={centered}
        color={color}
        disabled={disabled}
        focused={focused}
        forceSquareRatio={forceSquareRatio || !children}
        loading={loading}
        onClick={handleClick}
        ref={forwardedRef}
        size={size}
        skipLeftRounding={skipLeftRounding}
        skipRightRounding={skipRightRounding}
        tabIndex={0}
        type={type}
        variant={variant}
        withIconMargin={!!children}
        borderRadius={borderRadius}
      >
        {/*
          ButtonIcon can't be conditionally rendered due to the chromium bug:
          https://github.com/facebook/react/issues/11538
        */}
        <ButtonIcon {...buttonIconProps} icon={icon} loading={loading} position="left" isVisible={loading || !!icon} />
        {children && (
          <>
            <Space size="s10" horizontal />
            {children}
            <Space size="s10" horizontal />
          </>
        )}
        <ButtonIcon {...buttonIconProps} icon={iconRight} isVisible={!!iconRight} position="right" />
      </Styled.Button>
    )
  },
)
